import React from 'react';
import {classes, style} from './doppe-action-type-service-widget.st.css';
import {Icon} from '@wix/devzai-utils-react';
import {
    doppeHideableValueGetValue,
    doppeHideableValueIsHidden,
    doppeHideableValueIsVisibleAndNotEqualValue
} from '../../../client-server-common/types/doppe-hideable-value';
import {CommonClasses, FlexLayout} from '@wix/devzai-common-client';
import {
    DoppeActionTypeService,
    doppeActionTypeServiceResolveSettingsFromBoundEntity
} from '../doppe-action-type-service';
import {
    DoppeContactsIcon,
    DoppeDateIcon,
    DoppeLocationIcon,
    DoppePriceIcon,
    DoppeTimeIcon
} from '../../../doppe-static-assets/doppe-static-icons';
import {DoppeActionGoalType, DoppeActionType} from '../../../client-server-common/doppe-action-types/doppe-action-type';
import {
    CouponSourceType,
    DoppeActionTypeCouponView
} from '../../../components/doppe-action-type-coupon-view/doppe-action-type-coupon-view';
import {
    DoppeActionTypeWidgetInnerDescription
} from '../../../components/doppe-action-type-widget-inner-description/doppe-action-type-widget-inner-description';
import {
    bymoPageContextPropsResolveBusinessEntity,
    IDoppeViewerActionContext,
    useBymoPageContextProps
} from '../../../doppe-sdk';
import {doppeBusinessEntityDtoIsNotFound, useBymoPageProps} from '../../../client-server-common';
import {
    DoppeActionUnavailablePreview,
    DoppeActionUnavailableReason
} from '../../../components/doppe-action-unavailable-preview/doppe-action-unavailable-preview';
import {
    doppeExternalServiceProviderResolveBusinessEntityReference
} from '../../../client-server-common/types/doppe-external-services';
import {
    DoppeActionTypeWidgetInnerTitle
} from '../../../components/doppe-action-type-widget-inner-title/doppe-action-type-widget-inner-title';
import {HtmlObjectResizeMode} from '@wix/devzai-utils-common';
import {DoppePageMediaView} from '../../../components/doppe-page-media-view/doppe-page-media-view';
import {
    DoppeActionTypeWidgetLayout
} from '../../../components/doppe-action-type-widget-layout/doppe-action-type-widget-layout';
import {useDoppeViewerLocales} from '../../../client/doppe-viewer-locales/doppe-viewer-locales';
import {
    DoppeActionTypeWidgetContentLayoutNewWrapperSectionsAndButtons
} from '../../../components/doppe-action-type-widget-content-layout-new-wrapper-sections-and-buttons/doppe-action-type-widget-content-layout-new-wrapper-sections-and-buttons';

export const DoppeActionTypeServiceWidget = React.memo(function DoppeActionTypeServiceWidget(
    props: DoppeActionType.WidgetProps<DoppeActionTypeService.ActionSettings>
) {
    const {
        actionContext,

        className,
        ...htmlAttributes
    } = props;

    return (
        <DoppeActionTypeWidgetLayout
            {...htmlAttributes}
            className={style(classes.root, {}, className)}
            actionContext={actionContext}
        >
            <DoppeActionTypeServiceBindableWidget
                actionContext={actionContext}
                reportGoalAchieved={(goalType) => {
                    actionContext.reportGoalAchieved({
                        goalType: goalType,
                    })
                }}
                actionId={actionContext.action.id}
                actionSettings={actionContext.action.settings}
            />
        </DoppeActionTypeWidgetLayout>
    );
});

export const DoppeActionTypeServiceBindableWidget = React.memo(function DoppeActionTypeServiceBindableWidget(
    props: {
        actionSettings: DoppeActionTypeService.ActionSettings;
        reportGoalAchieved: (goalType: DoppeActionGoalType) => void;
        actionId: string;
        actionContext: IDoppeViewerActionContext;
        listItemId?: string;
    }
) {
    const {
        actionContext,
        actionSettings,
        reportGoalAchieved,
        actionId,
        listItemId
    } = props;

    const bymoPageContextProps = useBymoPageContextProps();
    const bymoPageProps = useBymoPageProps();

    const {
        serviceProvider,
        serviceProviderId
    } = actionSettings;

    if (serviceProvider !== null && serviceProviderId !== null) {
        const boundService = bymoPageContextPropsResolveBusinessEntity(
            bymoPageContextProps,
            doppeExternalServiceProviderResolveBusinessEntityReference(serviceProvider, serviceProviderId))

        if (!boundService || doppeBusinessEntityDtoIsNotFound(boundService)) {
            return (
                <DoppeActionTypeServiceWidgetContentUnavailable
                    reason={
                        !boundService ?
                            DoppeActionUnavailableReason.ContentLoadingError :
                            DoppeActionUnavailableReason.ContentUnavailable
                    }
                />
            )
        } else {
            return (
                <DoppeActionTypeServiceWidgetInner
                    actionContext={actionContext}
                    reportGoalAchieved={reportGoalAchieved}
                    actionId={actionId}
                    actionSettings={{
                        ...actionSettings,
                        ...doppeActionTypeServiceResolveSettingsFromBoundEntity(actionSettings, boundService, bymoPageProps),
                    }}
                    listItemId={listItemId}
                />
            )
        }
    } else {
        return (
            <DoppeActionTypeServiceWidgetInner
                actionContext={actionContext}
                reportGoalAchieved={reportGoalAchieved}
                actionId={actionId}
                actionSettings={actionSettings}
                listItemId={listItemId}
            />
        )
    }
});


export const DoppeActionTypeServiceWidgetContentUnavailable = React.memo(function DoppeActionTypeServiceWidgetContentUnavailable(
    props: {
        reason: DoppeActionUnavailableReason;
    }
) {
    const {
        reason
    } = props;

    const doppeViewerLocales = useDoppeViewerLocales();

    return (
        <DoppeActionUnavailablePreview
            reason={reason}
            title={doppeViewerLocales['contentUnavailableTitleService']}
        />
    )
});


export const DoppeActionTypeServiceWidgetInner = React.memo(function DoppeActionTypeServiceWidgetInner(
    props: {
        actionSettings: DoppeActionTypeService.ActionSettings;
        reportGoalAchieved: (goalType: DoppeActionGoalType) => void;
        actionId: string;
        actionContext: IDoppeViewerActionContext;
        listItemId?: string;
    }
) {
    const {
        actionSettings,
        reportGoalAchieved,
        actionId,
        actionContext,
        listItemId
    } = props;


    const {
        servicePageLink,
        priceData,
        description,
        serviceTitle,
        servicePeriod,
        serviceSchedule,
        serviceParticipants,
        location,
        serviceImages,
        couponCode,
    } = actionSettings;


    return (
        <>
            <DoppeActionTypeWidgetContentLayoutNewWrapperSectionsAndButtons
                actionContext={actionContext}
                reportGoalAchieved={reportGoalAchieved}
                mainMedia={serviceImages && serviceImages.length > 0 ? (
                    <DoppePageMediaView
                        className={style(classes.productMedia, {})}
                        mediaResource={serviceImages[0]}
                        layoutSpec={{autoHeight: true, maxHeight: 360}}
                        widthSpec={{contentRelativeWidth: 1}}
                        mediaResizeMode={HtmlObjectResizeMode.Cover}
                    />
                ) : null}
                sections={actionSettings.sections}
                buttons={actionSettings.ctaButtons}
                buttonsSettings={{
                    sourceURL: servicePageLink,
                    listItemId: listItemId,
                }}
            >
                <DoppeActionTypeWidgetInnerTitle>
                    {serviceTitle}
                </DoppeActionTypeWidgetInnerTitle>
                <DoppeActionTypeWidgetInnerDescription
                >
                    {description}
                </DoppeActionTypeWidgetInnerDescription>
                {
                    !doppeHideableValueIsHidden(priceData) || doppeHideableValueIsVisibleAndNotEqualValue(servicePeriod, '') || doppeHideableValueIsVisibleAndNotEqualValue(serviceParticipants, '') || doppeHideableValueIsVisibleAndNotEqualValue(serviceSchedule, '') || doppeHideableValueIsVisibleAndNotEqualValue(location, '') ?
                        <div
                            className={style(classes.detailsContainer, {
                                noTitleAndDescription: !doppeHideableValueIsVisibleAndNotEqualValue(serviceTitle, '') && !doppeHideableValueIsVisibleAndNotEqualValue(description, '')
                            }, FlexLayout.column.default)}
                        >
                            {
                                !doppeHideableValueIsHidden(priceData) ?
                                    <div
                                        className={style(classes.priceRow, {}, FlexLayout.row.alignToCenter)}
                                    >
                                        <Icon
                                            className={style(classes.serviceDetailsIcon, {})}
                                            icon={DoppePriceIcon}
                                        />
                                        <div
                                            className={style(classes.detailsText, {}, CommonClasses.ellipsis)}
                                        >
                                            {priceData.actualPrice}
                                        </div>
                                    </div>
                                    :
                                    null
                            }
                            {
                                doppeHideableValueIsVisibleAndNotEqualValue(servicePeriod, '') ?
                                    <div
                                        className={style(classes.servicePeriodRow, {}, FlexLayout.row.alignToCenter)}
                                    >
                                        <Icon
                                            className={style(classes.serviceDetailsIcon, {})}
                                            icon={DoppeTimeIcon}
                                        />
                                        <div
                                            className={style(classes.detailsText, {}, CommonClasses.ellipsis)}
                                        >
                                            {doppeHideableValueGetValue(servicePeriod)}
                                        </div>
                                    </div>
                                    :
                                    null
                            }
                            {
                                doppeHideableValueIsVisibleAndNotEqualValue(location, '') ?
                                    <div
                                        className={style(classes.serviceLocationRow, {}, FlexLayout.row.alignToCenter)}
                                    >
                                        <Icon
                                            className={style(classes.serviceDetailsIcon, {}, classes.serviceDetailsIcon)}
                                            icon={DoppeLocationIcon}
                                        />
                                        <div
                                            className={style(classes.detailsText, {}, CommonClasses.ellipsis)}
                                        >
                                            {doppeHideableValueGetValue(location)}
                                        </div>
                                    </div>
                                    :
                                    null
                            }
                            {
                                doppeHideableValueIsVisibleAndNotEqualValue(serviceSchedule, '') ?
                                    <div
                                        className={style(classes.servicePeriodRow, {}, FlexLayout.row.alignToCenter)}
                                    >
                                        <Icon
                                            className={style(classes.serviceDetailsIcon, {})}
                                            icon={DoppeDateIcon}
                                        />
                                        <div
                                            className={style(classes.detailsText, {}, CommonClasses.ellipsis)}
                                        >
                                            {doppeHideableValueGetValue(serviceSchedule)}
                                        </div>
                                    </div>
                                    :
                                    null
                            }
                            {
                                doppeHideableValueIsVisibleAndNotEqualValue(serviceParticipants, '') ?
                                    <div
                                        className={style(classes.serviceLocationRow, {}, FlexLayout.row.alignToCenter)}
                                    >
                                        <Icon
                                            className={style(classes.serviceDetailsIcon, {}, classes.serviceDetailsIcon)}
                                            icon={DoppeContactsIcon}
                                        />
                                        <div
                                            className={style(classes.detailsText, {}, CommonClasses.ellipsis)}
                                        >
                                            {doppeHideableValueGetValue(serviceParticipants)}
                                        </div>
                                    </div>
                                    :
                                    null
                            }

                        </div>
                        :
                        null
                }
                {
                    doppeHideableValueIsVisibleAndNotEqualValue(couponCode, '') ?
                        <DoppeActionTypeCouponView
                            className={style(classes.couponView, {})}
                            coupon={couponCode}
                            couponSourceType={CouponSourceType.Action}
                            actionId={actionId}
                            onCouponCopied={() => {
                                reportGoalAchieved(DoppeActionGoalType.CouponCopied);
                            }}
                        />
                        :
                        null
                }
            </DoppeActionTypeWidgetContentLayoutNewWrapperSectionsAndButtons>
        </>
    );
});


